import React, { useCallback, useEffect, useId, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import CommonPageHeader from "../../common/ui/page-header/CommonPageHeader";
import TableContainer from "../../common/ui/table-container/TableContainer";
import DataTable from "../../common/ui/data-table/DataTable";
import ExcelExportButton from "../../common/ui/excel-export-button/ExcelExportButton";
import PdfExportButton from "../../common/ui/pdf-export-button/PdfExportButton";
import { WebStorage, WebStorageNames } from "../../../utils";
import "../documents/Documents.css";
import "./TimeSheets.css";
import "./TimeSheetReportsPage.css";
import TimeSheetPersonCell from "./TimeSheetPersonCell";
import {
  addDays,
  formatRangeLabel,
  formatWeekRangeReadable,
  getWeekStartMonday,
  startOfMonth,
} from "./timesheetDateUtils";
import { getTimeSheetStatusLabel, TimeSheetStatus } from "./timesheetConstants";
import {
  colorFromProjectId,
  fetchEmployeePerformanceList,
  fetchEmployeePerformanceMatrixExport,
  fetchEmployeePerformanceMatrixPdf,
  fetchTimeSheetsStats,
  handleTimesheetApiError,
  triggerBlobDownload,
} from "./timesheetApi";

const MATRIX_PAGE_SIZE = 20;

/** @typedef {"month" | "week"} ReportsPeriodGranularity */

/** UI column key → API `sortBy` for EmployeePerformanceList */
const MATRIX_SORT_COL_TO_API = {
  employee: "employeeName",
  department: "departmentName",
  office: "officeName",
  total: "totalHours",
  billable: "billableHours",
  internal: "nonBillableHours",
  status: "aggregateStatus",
};

const matrixSortApiToColKey = (api) => {
  const e = Object.entries(MATRIX_SORT_COL_TO_API).find(([, v]) => v === api);
  return e ? e[0] : null;
};

const rowBillableRatioPct = (row) => {
  const tot = Number(row.totalHours) || 0;
  if (tot <= 0) return 0;
  return ((Number(row.billableHours) || 0) / tot) * 100;
};

const getRoleId = () => {
  const r = WebStorage.getLocalStore(WebStorageNames.RoleId) ?? localStorage.getItem("roleId");
  const n = parseInt(String(r), 10);
  return Number.isFinite(n) ? n : null;
};

const isAdminRole = (roleId) => roleId === 1 || roleId === 2;
const isManagerOrAdminRole = (roleId) => roleId === 1 || roleId === 2 || roleId === 3;

const submissionStatusLabel = (status) => {
  const s = String(status ?? "").trim();
  if (/^-?\d+$/.test(s)) return getTimeSheetStatusLabel(Number(s));
  return s || "—";
};

const parseSubmissionStatus = (raw) => {
  const s = String(raw ?? "").trim();
  if (/^-?\d+$/.test(s)) {
    const n = Number(s);
    if (n >= TimeSheetStatus.Draft && n <= TimeSheetStatus.Returned) return n;
    return null;
  }
  const lower = s.toLowerCase();
  const byName = {
    draft: TimeSheetStatus.Draft,
    submitted: TimeSheetStatus.Submitted,
    approved: TimeSheetStatus.Approved,
    rejected: TimeSheetStatus.Rejected,
    returned: TimeSheetStatus.Returned,
  };
  return byName[lower] ?? null;
};

const matrixAggregateBadgeClass = (raw) => {
  const n = parseSubmissionStatus(raw);
  if (n === TimeSheetStatus.Approved) return "ts-matrix-status ts-matrix-status--approved";
  if (n === TimeSheetStatus.Rejected) return "ts-matrix-status ts-rep-matrix-status--rejected";
  if (n === TimeSheetStatus.Returned) return "ts-matrix-status ts-rep-matrix-status--returned";
  if (n === TimeSheetStatus.Draft) return "ts-matrix-status ts-rep-matrix-status--draft";
  if (n === TimeSheetStatus.Submitted) return "ts-matrix-status ts-rep-matrix-status--submitted";
  return "ts-matrix-status ts-matrix-status--pending";
};

const submissionStatusDotClass = (raw) => {
  const n = parseSubmissionStatus(raw);
  switch (n) {
    case TimeSheetStatus.Draft:
      return "ts-rep-submission-dot--draft";
    case TimeSheetStatus.Submitted:
      return "ts-rep-submission-dot--submitted";
    case TimeSheetStatus.Approved:
      return "ts-rep-submission-dot--approved";
    case TimeSheetStatus.Rejected:
      return "ts-rep-submission-dot--rejected";
    case TimeSheetStatus.Returned:
      return "ts-rep-submission-dot--returned";
    default:
      return "ts-rep-submission-dot--default";
  }
};

const SUBMISSION_STATUS_SORT_ORDER = [
  TimeSheetStatus.Draft,
  TimeSheetStatus.Submitted,
  TimeSheetStatus.Approved,
  TimeSheetStatus.Rejected,
  TimeSheetStatus.Returned,
];

/** API keys: Billable | Internal | Overtime | Forecast — hide Forecast in UI */
const isForecastProgressSlice = (slice) => {
  const k = String(slice?.key ?? "").toLowerCase();
  const l = String(slice?.label ?? "").toLowerCase();
  return k.includes("forecast") || l.includes("forecast");
};

const progressSliceFillClass = (key) => {
  const k = String(key || "").toLowerCase();
  if (k.includes("billable")) return "ts-rep-slice-row__fill--billable";
  if (k.includes("internal")) return "ts-rep-slice-row__fill--internal";
  if (k.includes("overtime")) return "ts-rep-slice-row__fill--overtime";
  return "ts-rep-slice-row__fill--default";
};

const KPI_BILLABLE_DONUT_R = 44;
const KPI_BILLABLE_DONUT_C = 2 * Math.PI * KPI_BILLABLE_DONUT_R;

const TimeSheetReportsPage = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const kpiBillableGradId = `ts-rep-kpi-bill-${useId().replace(/[^a-zA-Z0-9_-]/g, "")}`;
  const roleId = getRoleId();
  const canViewReports = isManagerOrAdminRole(roleId);
  const adminUser = isAdminRole(roleId);

  const [periodGranularity, setPeriodGranularity] = useState(
    /** @type {ReportsPeriodGranularity} */ ("month")
  );
  const [monthAnchor, setMonthAnchor] = useState(() => {
    const n = new Date();
    return new Date(n.getFullYear(), n.getMonth(), 1);
  });
  const [weekAnchorMonday, setWeekAnchorMonday] = useState(() => getWeekStartMonday(new Date()));

  const { periodStart, periodEnd, periodKey, rangeLabel, periodHeading } = useMemo(() => {
    if (periodGranularity === "week") {
      const start = new Date(weekAnchorMonday);
      start.setHours(0, 0, 0, 0);
      const end = addDays(start, 6);
      end.setHours(23, 59, 59, 999);
      const y = start.getFullYear();
      const mo = String(start.getMonth() + 1).padStart(2, "0");
      const d = String(start.getDate()).padStart(2, "0");
      return {
        periodStart: start,
        periodEnd: end,
        periodKey: `week-${y}-${mo}-${d}`,
        rangeLabel: formatRangeLabel(start, end),
        periodHeading: formatWeekRangeReadable(start, end),
      };
    }
    const y = monthAnchor.getFullYear();
    const m = monthAnchor.getMonth();
    const start = new Date(y, m, 1);
    start.setHours(0, 0, 0, 0);
    const end = new Date(y, m + 1, 0);
    end.setHours(23, 59, 59, 999);
    return {
      periodStart: start,
      periodEnd: end,
      periodKey: `month-${y}-${m}`,
      rangeLabel: formatRangeLabel(start, end),
      periodHeading: new Intl.DateTimeFormat(undefined, { month: "long", year: "numeric" }).format(monthAnchor),
    };
  }, [monthAnchor, periodGranularity, weekAnchorMonday]);

  const [adminStats, setAdminStats] = useState(null);
  const [adminLoading, setAdminLoading] = useState(false);
  const [matrixPaged, setMatrixPaged] = useState(null);
  const [matrixLoading, setMatrixLoading] = useState(false);
  const [matrixPage, setMatrixPage] = useState(1);
  const [matrixSortByApi, setMatrixSortByApi] = useState("totalHours");
  const [matrixSortDesc, setMatrixSortDesc] = useState(true);
  const [exportingFormat, setExportingFormat] = useState(null);

  const shiftMonth = useCallback((delta) => {
    setMonthAnchor((prev) => new Date(prev.getFullYear(), prev.getMonth() + delta, 1));
  }, []);

  const shiftWeek = useCallback((delta) => {
    setWeekAnchorMonday((prev) => addDays(prev, delta * 7));
  }, []);

  const goThisMonth = useCallback(() => {
    const n = new Date();
    setMonthAnchor(new Date(n.getFullYear(), n.getMonth(), 1));
  }, []);

  const goThisWeek = useCallback(() => {
    setWeekAnchorMonday(getWeekStartMonday(new Date()));
  }, []);

  const handleGranularityChange = useCallback(
    (next) => {
      if (next === periodGranularity) return;
      if (next === "week") {
        setWeekAnchorMonday(getWeekStartMonday(monthAnchor));
      } else {
        setMonthAnchor(startOfMonth(weekAnchorMonday));
      }
      setPeriodGranularity(next);
    },
    [monthAnchor, periodGranularity, weekAnchorMonday]
  );

  useEffect(() => {
    if (!adminUser) {
      setAdminStats(null);
      return undefined;
    }
    let cancelled = false;
    setAdminLoading(true);
    fetchTimeSheetsStats({ periodStart, periodEnd })
      .then((data) => {
        if (!cancelled) setAdminStats(data);
      })
      .catch((err) => {
        if (!cancelled) {
          setAdminStats(null);
          handleTimesheetApiError(err, t("app.timesheets.reports.adminStatsLoadFailed"));
        }
      })
      .finally(() => {
        if (!cancelled) setAdminLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [adminUser, periodKey, periodStart, periodEnd, t]);

  useEffect(() => {
    setMatrixPage(1);
  }, [periodKey]);

  useEffect(() => {
    if (!adminUser) {
      setMatrixPaged(null);
      return undefined;
    }
    let cancelled = false;
    setMatrixLoading(true);
    fetchEmployeePerformanceList({
      period: { periodStart, periodEnd },
      page: matrixPage,
      take: MATRIX_PAGE_SIZE,
      sortBy: matrixSortByApi,
      sortDescending: matrixSortDesc,
    })
      .then((data) => {
        if (!cancelled) setMatrixPaged(data);
      })
      .catch((err) => {
        if (!cancelled) {
          setMatrixPaged(null);
          handleTimesheetApiError(err, t("app.timesheets.reports.matrixListLoadFailed"));
        }
      })
      .finally(() => {
        if (!cancelled) setMatrixLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [adminUser, periodKey, matrixPage, matrixSortByApi, matrixSortDesc, periodStart, periodEnd, t]);

  const handleMatrixSort = useCallback((colKey) => {
    const api = MATRIX_SORT_COL_TO_API[colKey];
    if (!api) return;
    setMatrixSortByApi((prev) => {
      if (prev === api) {
        setMatrixSortDesc((d) => !d);
        return prev;
      }
      setMatrixSortDesc(true);
      return api;
    });
    setMatrixPage(1);
  }, []);

  const matrixSortColKey = useMemo(() => matrixSortApiToColKey(matrixSortByApi), [matrixSortByApi]);

  const activeProjectsCount = useMemo(() => {
    const rows = adminStats?.hoursByProject ?? [];
    return rows.filter((p) => (Number(p.hours) || 0) > 0).length;
  }, [adminStats?.hoursByProject]);

  const billableRatioKpi = useMemo(() => {
    const p = Number(adminStats?.totals?.billableRatioPercent);
    if (!Number.isFinite(p)) return 0;
    return Math.min(100, Math.max(0, p));
  }, [adminStats?.totals?.billableRatioPercent]);

  const billableDonutOffset = KPI_BILLABLE_DONUT_C * (1 - billableRatioKpi / 100);

  const pieConicGradient = useMemo(() => {
    const rows = adminStats?.hoursByProject ?? [];
    if (!rows.length) return null;
    let acc = 0;
    const stops = rows.map((row) => {
      const from = acc;
      acc += Math.max(0, Number(row.percentOfTotal) || 0);
      return `${colorFromProjectId(row.projectId)} ${from}% ${acc}%`;
    });
    return `conic-gradient(${stops.join(", ")})`;
  }, [adminStats?.hoursByProject]);

  const utilizationMax = useMemo(() => {
    const pts = adminStats?.utilizationThread ?? [];
    let m = 0;
    for (const p of pts) m = Math.max(m, Number(p.totalHours) || 0);
    return m > 0 ? m : 1;
  }, [adminStats?.utilizationThread]);

  const matrixColumns = useMemo(
    () => [
      { key: "employee", label: t("app.timesheets.reports.matrixColEmployee"), sortable: true },
      { key: "department", label: t("app.timesheets.reports.matrixColDepartment"), sortable: true },
      { key: "office", label: t("app.timesheets.reports.matrixColOffice"), sortable: true },
      { key: "total", label: t("app.timesheets.reports.matrixColTotalHours"), sortable: true, headerClassName: "text-end" },
      { key: "billable", label: t("app.timesheets.reports.matrixColBillable"), sortable: true, headerClassName: "text-end" },
      { key: "internal", label: t("app.timesheets.reports.matrixColNonBillable"), sortable: true, headerClassName: "text-end" },
      { key: "billablePct", label: t("app.timesheets.reports.matrixColBillablePct"), sortable: false, headerClassName: "text-end" },
      { key: "count", label: t("app.timesheets.reports.matrixColTimesheets"), sortable: false, headerClassName: "text-end" },
      { key: "projects", label: t("app.timesheets.reports.matrixColProjects"), sortable: false, headerClassName: "text-start" },
      {
        key: "status",
        label: t("app.timesheets.reports.matrixColAggregateStatus"),
        sortable: true,
        headerClassName: "text-center",
      },
    ],
    [t]
  );

  const matrixRows = matrixPaged?.items ?? [];

  const matrixPagerRange = useMemo(() => {
    if (!matrixPaged) return null;
    const total = matrixPaged.totalCount;
    const totalPages = Math.max(1, matrixPaged.totalPages || 1);
    if (total <= 0) {
      return { from: 0, to: 0, total: 0, totalPages: 0 };
    }
    const from = (matrixPaged.page - 1) * matrixPaged.take + 1;
    const to = Math.min(matrixPaged.page * matrixPaged.take, total);
    return { from, to, total, totalPages: matrixPaged.totalPages || totalPages };
  }, [matrixPaged]);

  const progressSlicesVisible = useMemo(() => {
    const raw = adminStats?.progress?.slices ?? [];
    return raw.filter((sl) => !isForecastProgressSlice(sl));
  }, [adminStats?.progress?.slices]);

  const submissionRowsSorted = useMemo(() => {
    const rows = adminStats?.submissionStatus ?? [];
    const rank = (raw) => {
      const n = parseSubmissionStatus(raw);
      const i = n == null ? -1 : SUBMISSION_STATUS_SORT_ORDER.indexOf(n);
      return i === -1 ? 100 : i;
    };
    return [...rows].sort((a, b) => rank(a.status) - rank(b.status));
  }, [adminStats?.submissionStatus]);

  const submissionTimesheetsTotal = useMemo(
    () => submissionRowsSorted.reduce((acc, r) => acc + (Number(r.count) || 0), 0),
    [submissionRowsSorted]
  );

  /** Sum of visible progress slices (billable + internal + overtime, etc.) */
  const hoursMixBreakdown = useMemo(() => {
    const findSlice = (needle) => {
      const n = needle.toLowerCase();
      return progressSlicesVisible.find(
        (s) =>
          String(s.key || "")
            .toLowerCase()
            .includes(n) || String(s.label || "").toLowerCase().includes(n)
      );
    };
    const h = (needle) => {
      const sl = findSlice(needle);
      return sl ? Number(sl.hours) || 0 : 0;
    };
    const total = progressSlicesVisible.reduce((acc, sl) => acc + (Number(sl.hours) || 0), 0);
    return {
      total,
      billable: h("billable"),
      internal: h("internal"),
      overtime: h("overtime"),
    };
  }, [progressSlicesVisible]);

  const hoursMixTotalHours = hoursMixBreakdown.total;
  const mixBillableSharePct =
    hoursMixTotalHours > 0 ? (hoursMixBreakdown.billable / hoursMixTotalHours) * 100 : 0;
  const avgHoursPerTimesheetRecord =
    submissionTimesheetsTotal > 0 ? hoursMixTotalHours / submissionTimesheetsTotal : 0;

  const handleExportMatrix = async (format) => {
    setExportingFormat(format);
    try {
      const period = { periodStart, periodEnd };
      const blob =
        format === "pdf"
          ? await fetchEmployeePerformanceMatrixPdf(period)
          : await fetchEmployeePerformanceMatrixExport(format, period);
      const ext = format === "xlsx" ? "xlsx" : format === "csv" ? "csv" : "pdf";
      const stamp =
        periodGranularity === "week"
          ? `${periodStart.getFullYear()}-${String(periodStart.getMonth() + 1).padStart(2, "0")}-${String(periodStart.getDate()).padStart(2, "0")}`
          : `${periodStart.getFullYear()}-${String(periodStart.getMonth() + 1).padStart(2, "0")}`;
      triggerBlobDownload(blob, `employee-performance-matrix-${stamp}.${ext}`);
    } catch (err) {
      handleTimesheetApiError(err, t("app.timesheets.reports.exportMatrixFailed"));
    } finally {
      setExportingFormat(null);
    }
  };

  return (
    <div className="documents-page ts-page ts-reports-page container-fluid px-3 px-md-4 py-4">
      <CommonPageHeader
        icon={<i className="bi bi-bar-chart-line" />}
        title={t("app.timesheets.reports.title")}
        description={t("app.timesheets.reports.description")}
      />

      {!canViewReports ? (
        <div className="alert alert-info border-0 shadow-sm" role="status">
          {t("app.timesheets.reports.accessDenied")}
        </div>
      ) : (
        <>
          <section className="ts-rep-period" aria-label={t("app.timesheets.reports.periodAria")}>
            <div
              className="ts-rep-period__granularity"
              role="group"
              aria-label={t("app.timesheets.reports.periodGranularityAria")}
            >
              <button
                type="button"
                className={`ts-rep-period__mode${periodGranularity === "month" ? " ts-rep-period__mode--active" : ""}`}
                aria-pressed={periodGranularity === "month"}
                onClick={() => handleGranularityChange("month")}
              >
                {t("app.timesheets.reports.periodModeMonth")}
              </button>
              <button
                type="button"
                className={`ts-rep-period__mode${periodGranularity === "week" ? " ts-rep-period__mode--active" : ""}`}
                aria-pressed={periodGranularity === "week"}
                onClick={() => handleGranularityChange("week")}
              >
                {t("app.timesheets.reports.periodModeWeek")}
              </button>
            </div>
            <div className="ts-rep-period__nav">
              <button
                type="button"
                className="ts-rep-period__chev"
                aria-label={
                  periodGranularity === "week"
                    ? t("app.timesheets.reports.periodPrevWeek")
                    : t("app.timesheets.reports.periodPrevMonth")
                }
                onClick={() => (periodGranularity === "week" ? shiftWeek(-1) : shiftMonth(-1))}
              >
                <i className="bi bi-chevron-left" aria-hidden />
              </button>
              <h2 className="ts-rep-period__title">{periodHeading}</h2>
              <button
                type="button"
                className="ts-rep-period__chev"
                aria-label={
                  periodGranularity === "week"
                    ? t("app.timesheets.reports.periodNextWeek")
                    : t("app.timesheets.reports.periodNextMonth")
                }
                onClick={() => (periodGranularity === "week" ? shiftWeek(1) : shiftMonth(1))}
              >
                <i className="bi bi-chevron-right" aria-hidden />
              </button>
            </div>
            <p className="ts-rep-period__range mb-0">
              <span className="visually-hidden">{t("app.timesheets.reports.periodLabel")}: </span>
              {rangeLabel}
              {periodGranularity === "week" ? (
                <>
                  {" "}
                  <span className="ts-rep-period__week-hint">({t("app.timesheets.reports.periodWeekMonSunHint")})</span>
                </>
              ) : null}
            </p>
            <button
              type="button"
              className="btn btn-sm btn-outline-primary ts-rep-period__this"
              onClick={periodGranularity === "week" ? goThisWeek : goThisMonth}
            >
              {periodGranularity === "week"
                ? t("app.timesheets.reports.periodThisWeek")
                : t("app.timesheets.reports.periodThisMonth")}
            </button>
          </section>

          {adminUser ? (
            <section className="ts-kpi-grid ts-rep-kpi-grid" aria-label={t("app.timesheets.reports.kpiAria")}>
              <div className="ts-kpi-card ts-kpi-card--dual">
                <div className="ts-kpi-dual-cell">
                  <p className="ts-kpi-label">{t("app.timesheets.reports.kpiTimesheetRecords")}</p>
                  <p className="ts-kpi-value">{submissionTimesheetsTotal.toLocaleString()}</p>
                  <p className="ts-kpi-foot text-muted small mb-0">{t("app.timesheets.reports.kpiTimesheetRecordsHint")}</p>
                </div>
                <div className="ts-kpi-dual-cell">
                  <p className="ts-kpi-label">{t("app.timesheets.reports.kpiAvgHoursPerRecord")}</p>
                  <p className="ts-kpi-value">
                    {submissionTimesheetsTotal > 0
                      ? `${avgHoursPerTimesheetRecord.toLocaleString(undefined, { maximumFractionDigits: 1 })}h`
                      : "—"}
                  </p>
                  <p className="ts-kpi-foot text-muted small mb-0">{t("app.timesheets.reports.kpiAvgHoursPerRecordHint")}</p>
                </div>
              </div>
              <div className="ts-kpi-card ts-kpi-card--ratio">
                <p className="ts-kpi-label">{t("app.timesheets.reports.totalsBillableRatio")}</p>
                <div className="ts-kpi-ratio-body">
                  <svg
                    className="ts-kpi-donut"
                    width="112"
                    height="112"
                    viewBox="0 0 112 112"
                    role="img"
                    aria-label={t("app.timesheets.reports.kpiBillableRatioAria", { pct: billableRatioKpi.toFixed(1) })}
                  >
                    <defs>
                      <linearGradient id={kpiBillableGradId} x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#22c55e" />
                        <stop offset="100%" stopColor="#0d9488" />
                      </linearGradient>
                    </defs>
                    <circle
                      className="ts-kpi-donut__track"
                      cx="56"
                      cy="56"
                      r={KPI_BILLABLE_DONUT_R}
                      transform="rotate(-90 56 56)"
                    />
                    <circle
                      className="ts-kpi-donut__arc"
                      cx="56"
                      cy="56"
                      r={KPI_BILLABLE_DONUT_R}
                      transform="rotate(-90 56 56)"
                      stroke={`url(#${kpiBillableGradId})`}
                      strokeDasharray={`${KPI_BILLABLE_DONUT_C}`}
                      strokeDashoffset={billableDonutOffset}
                    />
                    <text x="56" y="60" textAnchor="middle" className="ts-kpi-donut__pct-wrap">
                      <tspan className="ts-kpi-donut__pct-num">{billableRatioKpi.toFixed(1)}</tspan>
                      <tspan className="ts-kpi-donut__pct-suffix">%</tspan>
                    </text>
                  </svg>
                </div>
              </div>
              <div className="ts-kpi-card">
                <p className="ts-kpi-label">{t("app.timesheets.reports.kpiActiveProjects")}</p>
                <p className="ts-kpi-value">{activeProjectsCount.toLocaleString()}</p>
                <p className="ts-kpi-foot text-muted small mb-0">{t("app.timesheets.reports.kpiActiveProjectsHint")}</p>
              </div>
            </section>
          ) : null}

          {!adminUser ? (
            <div className="alert alert-light border shadow-sm" role="status">
              {t("app.timesheets.reports.managerAnalyticsHint")}
            </div>
          ) : null}

          {adminUser ? (
            <>
              <section className="ts-reports-bento" aria-label={t("app.timesheets.reports.progressFromApi")}>
                <div className="ts-progress-card ts-rep-hours-mix">
                  <div className="w-100">
                    <div className="ts-rep-hours-mix__head">
                      <p className="ts-progress-eyebrow">{t("app.timesheets.reports.progressFromApi")}</p>
                      <p className="documents-page-description small mb-0">{rangeLabel}</p>
                    </div>
                    {progressSlicesVisible.length > 0 ? (
                      <div className="ts-rep-hours-mix__hero" aria-label={t("app.timesheets.reports.hoursMixTotalTitle")}>
                        <div className="ts-rep-hours-mix__hero-main">
                          <span className="ts-rep-hours-mix__hero-icon" aria-hidden>
                            <i className="bi bi-pie-chart-fill" />
                          </span>
                          <div className="ts-rep-hours-mix__hero-copy">
                            <p className="ts-rep-hours-mix__hero-label">{t("app.timesheets.reports.hoursMixTotalTitle")}</p>
                            <p className="ts-rep-hours-mix__hero-value">
                              {hoursMixTotalHours.toLocaleString(undefined, { maximumFractionDigits: 1 })}
                              <span className="ts-rep-hours-mix__hero-unit">h</span>
                            </p>
                            <p className="ts-rep-hours-mix__hero-hint">{t("app.timesheets.reports.hoursMixTotalHint")}</p>
                          </div>
                        </div>
                        <div className="ts-rep-hours-mix__hero-pills">
                          <div className="ts-rep-hours-mix__pill">
                            <span className="ts-rep-hours-mix__pill-label">{t("app.timesheets.reports.hoursMixPillBillableShare")}</span>
                            <span className="ts-rep-hours-mix__pill-value">{mixBillableSharePct.toFixed(1)}%</span>
                          </div>
                          <div className="ts-rep-hours-mix__pill">
                            <span className="ts-rep-hours-mix__pill-label">{t("app.timesheets.reports.hoursMixPillAvgPerRecord")}</span>
                            <span className="ts-rep-hours-mix__pill-value">
                              {submissionTimesheetsTotal > 0
                                ? `${avgHoursPerTimesheetRecord.toFixed(1)}h`
                                : "—"}
                            </span>
                          </div>
                          <div className="ts-rep-hours-mix__pill">
                            <span className="ts-rep-hours-mix__pill-label">{t("app.timesheets.reports.hoursMixPillRecords")}</span>
                            <span className="ts-rep-hours-mix__pill-value">{submissionTimesheetsTotal.toLocaleString()}</span>
                          </div>
                        </div>
                      </div>
                    ) : null}
                    {progressSlicesVisible.map((sl) => (
                      <div key={sl.key || sl.label} className="ts-rep-slice-row">
                        <span className="ts-rep-slice-row__label">{sl.label || sl.key}</span>
                        <div className="ts-rep-slice-row__track">
                          <div
                            className={`ts-rep-slice-row__fill ${progressSliceFillClass(sl.key)}`}
                            style={{ width: `${Math.min(100, Math.max(0, sl.percentOfTotal))}%` }}
                          />
                        </div>
                        <span className="ts-rep-slice-row__hrs">{sl.hours.toFixed(1)}h</span>
                      </div>
                    ))}
                    {progressSlicesVisible.length > 0 ? (
                      <div className="ts-rep-slice-row ts-rep-slice-row--total">
                        <span className="ts-rep-slice-row__label ts-rep-slice-row__label--total">
                          {t("app.timesheets.reports.hoursMixTotalFooter")}
                        </span>
                        <div className="ts-rep-slice-row__track ts-rep-slice-row__track--total">
                          <div
                            className="ts-rep-slice-row__fill ts-rep-slice-row__fill--total"
                            style={{ width: "100%" }}
                          />
                        </div>
                        <span className="ts-rep-slice-row__hrs ts-rep-slice-row__hrs--total">
                          {hoursMixTotalHours.toFixed(1)}h
                        </span>
                      </div>
                    ) : null}
                    {!adminLoading && !progressSlicesVisible.length ? (
                      <p className="text-muted small mb-0">{t("app.timesheets.reports.matrixEmpty")}</p>
                    ) : null}
                  </div>
                </div>

                <div
                  className="ts-submission-card ts-rep-submission-card"
                  aria-label={t("app.timesheets.reports.submissionFromApi")}
                >
                  <div className="w-100">
                    <div className="ts-rep-submission-card__head">
                      <h3 className="ts-rep-submission-card__title">{t("app.timesheets.reports.submissionStatusTitle")}</h3>
                      <p className="ts-rep-submission-card__sub">{t("app.timesheets.reports.submissionFromApi")}</p>
                    </div>
                    <div className="ts-rep-submission-rows">
                      {submissionRowsSorted.map((row) => (
                        <div key={String(row.status)} className="ts-submission-row ts-rep-submission-row">
                          <div className="ts-submission-row-left">
                            <span
                              className={`ts-rep-submission-dot ${submissionStatusDotClass(row.status)}`}
                              aria-hidden
                            />
                            <span className="ts-submission-label">{submissionStatusLabel(row.status)}</span>
                          </div>
                          <span className="ts-rep-submission-count">{row.count}</span>
                        </div>
                      ))}
                    </div>
                    {!adminLoading && !submissionRowsSorted.length ? (
                      <p className="text-muted small mb-0">{t("app.timesheets.reports.matrixEmpty")}</p>
                    ) : null}
                  </div>
                  <button type="button" className="ts-submission-history-btn" onClick={() => history.push("/timesheets/time-log")}>
                    {t("app.timesheets.reports.viewHistory")}
                  </button>
                </div>
              </section>

              <section className="ts-reports-charts" aria-label={t("app.timesheets.reports.chartsAria")}>
                <div className="ts-card">
                  <div className="ts-chart-head" style={{ marginBottom: "1.25rem" }}>
                    <div>
                      <h3 className="ts-chart-title mb-1">{t("app.timesheets.reports.utilizationFromApi")}</h3>
                      <p className="documents-page-description mb-0">{t("app.timesheets.reports.chartUtilizationSubtitle")}</p>
                    </div>
                    <div className="ts-legend ts-reports-charts__legend" style={{ border: "none", padding: 0, margin: 0 }}>
                      <div className="ts-legend-item">
                        <span className="ts-legend-swatch ts-reports-charts__swatch ts-reports-charts__swatch--billable" />
                        {t("app.timesheets.reports.legendBillable")}
                      </div>
                      <div className="ts-legend-item">
                        <span className="ts-legend-swatch ts-reports-charts__swatch ts-reports-charts__swatch--nonbillable" />
                        {t("app.timesheets.reports.legendNonBillable")}
                      </div>
                    </div>
                  </div>
                  <div className="ts-trend-chart">
                    {(adminStats?.utilizationThread ?? []).map((w) => {
                      const total = Number(w.totalHours) || 0;
                      const bill = Number(w.billableHours) || 0;
                      const billPct = utilizationMax > 0 ? (bill / utilizationMax) * 100 : 0;
                      const restPct = utilizationMax > 0 ? (Math.max(0, total - bill) / utilizationMax) * 100 : 0;
                      return (
                        <div key={w.label || w.periodStart} className="ts-trend-col">
                          <div className="ts-trend-stack">
                            <div className="ts-trend-seg-b" style={{ height: `${billPct}%` }} />
                            <div className="ts-trend-seg-nb" style={{ height: `${restPct}%` }} />
                          </div>
                          <span className="ts-bar-label" title={w.label}>
                            {w.label || "—"}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                  {!adminLoading && !(adminStats?.utilizationThread ?? []).length ? (
                    <p className="text-muted small mb-0">{t("app.timesheets.reports.matrixEmpty")}</p>
                  ) : null}
                </div>

                <div className="ts-card ts-pie-card">
                  <h3 className="ts-chart-title mb-1">{t("app.timesheets.reports.hoursByProjectFromApi")}</h3>
                  <p className="documents-page-description mb-3">{t("app.timesheets.reports.pieSubtitle")}</p>
                  <div className="ts-pie-visual-wrap">
                    <div
                      className="ts-pie-donut"
                      style={{
                        background: pieConicGradient ?? "conic-gradient(#e2e8f0 0% 100%)",
                      }}
                      aria-hidden
                    >
                      <div className="ts-pie-donut-hole" />
                    </div>
                    <div className="ts-pie-center-label ts-reports-charts__pie-center">
                      <span className="ts-reports-charts__pie-pct">100%</span>
                      <span className="ts-reports-charts__pie-label">{t("app.timesheets.reports.pieCenter")}</span>
                    </div>
                  </div>
                  <div className="d-flex flex-column gap-1 mt-2">
                    {(adminStats?.hoursByProject ?? []).map((p) => (
                      <div key={p.projectId || p.projectTitle} className="ts-rep-project-row">
                        <span className="d-flex align-items-center gap-2 min-w-0">
                          <span
                            style={{
                              width: 10,
                              height: 10,
                              borderRadius: 2,
                              background: colorFromProjectId(p.projectId),
                              flexShrink: 0,
                            }}
                            aria-hidden
                          />
                          <span className="fw-medium text-truncate">{p.projectTitle}</span>
                          {p.isInternal ? <small className="text-muted">({t("app.timesheets.reports.internalHours")})</small> : null}
                        </span>
                        <span className="fw-bold text-nowrap">{p.percentOfTotal.toFixed(1)}%</span>
                      </div>
                    ))}
                  </div>
                  {!adminLoading && !(adminStats?.hoursByProject ?? []).length ? (
                    <p className="text-muted small mb-0">{t("app.timesheets.reports.matrixEmpty")}</p>
                  ) : null}
                </div>
              </section>

              <section aria-label={t("app.timesheets.reports.matrixAria")}>
                <div className="ts-rep-matrix-head documents-table-toolbar">
                  <h3 className="ts-chart-title ts-rep-matrix-head__title">{t("app.timesheets.reports.matrixTitle")}</h3>
                  <div
                    className="ts-rep-matrix-export-actions"
                    role="group"
                    aria-label={t("app.timesheets.reports.exportMatrixActionsAria")}
                  >
                    <div className="documents-compliance-export-wrap">
                      <ExcelExportButton
                        className="documents-compliance-export-btn"
                        onClick={() => handleExportMatrix("xlsx")}
                        disabled={!!exportingFormat}
                        loading={exportingFormat === "xlsx"}
                        title={t("app.timesheets.reports.exportExcelTitle")}
                        label={t("app.timesheets.reports.exportMatrixXlsx")}
                        loadingLabel={t("app.timesheets.reports.exportMatrixLoading")}
                      />
                    </div>
                    <button
                      type="button"
                      className="ts-rep-export-csv-btn"
                      disabled={!!exportingFormat}
                      title={t("app.timesheets.reports.exportCsvTitle")}
                      onClick={() => handleExportMatrix("csv")}
                    >
                      {exportingFormat === "csv" ? (
                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden />
                      ) : (
                        <i className="bi bi-filetype-csv" aria-hidden />
                      )}
                      <span>{exportingFormat === "csv" ? t("app.timesheets.reports.exportMatrixLoading") : t("app.timesheets.reports.exportMatrixCsv")}</span>
                    </button>
                    <PdfExportButton
                      onClick={() => handleExportMatrix("pdf")}
                      disabled={!!exportingFormat}
                      loading={exportingFormat === "pdf"}
                      title={t("app.timesheets.reports.exportPdfTitle")}
                      label={t("app.timesheets.reports.exportMatrixPdf")}
                      loadingLabel={t("app.timesheets.reports.exportMatrixLoading")}
                    />
                  </div>
                </div>
                <TableContainer className="documents-table-wrap ts-rep-matrix-table-wrap">
                  <DataTable
                    className="documents-table ts-rep-matrix-data-table"
                    columns={matrixColumns}
                    rows={matrixRows}
                    getRowKey={(r) => r.userId || r.email}
                    sortKey={matrixSortColKey}
                    sortDir={matrixSortDesc ? "desc" : "asc"}
                    onSort={handleMatrixSort}
                    renderRow={(row) => (
                      <>
                        <td className="align-middle">
                          <TimeSheetPersonCell
                            variant="approval"
                            name={row.employeeName}
                            email={row.email}
                            photoUrl={row.profileImageUrl || undefined}
                          />
                        </td>
                        <td
                          className="align-middle small text-secondary ts-rep-matrix-meta-cell"
                          title={row.departmentName || undefined}
                        >
                          {row.departmentName || "—"}
                        </td>
                        <td
                          className="align-middle small text-secondary ts-rep-matrix-meta-cell"
                          title={row.officeName || undefined}
                        >
                          {row.officeName || "—"}
                        </td>
                        <td className="text-end align-middle fw-semibold ts-rep-matrix-num">{row.totalHours.toFixed(1)}</td>
                        <td className="text-end align-middle fw-bold ts-rep-matrix-billable ts-rep-matrix-num">
                          {row.billableHours.toFixed(1)}
                        </td>
                        <td className="text-end align-middle text-muted ts-rep-matrix-num">{row.nonBillableHours.toFixed(1)}</td>
                        <td className="text-end align-middle ts-rep-matrix-num">{rowBillableRatioPct(row).toFixed(1)}%</td>
                        <td className="text-end align-middle ts-rep-matrix-num">{row.timesheetCount}</td>
                        <td className="align-middle ts-rep-matrix-projects small text-secondary text-start" title={row.projects || undefined}>
                          {row.projects || "—"}
                        </td>
                        <td className="align-middle text-center">
                          <span className={matrixAggregateBadgeClass(row.aggregateTimesheetStatus)}>
                            {submissionStatusLabel(row.aggregateTimesheetStatus)}
                          </span>
                        </td>
                      </>
                    )}
                    loading={matrixLoading}
                    loadingMessage={t("app.timesheets.reports.matrixLoading")}
                    emptyMessage={t("app.timesheets.reports.matrixEmpty")}
                    stateCellStyle={{ textAlign: "center", padding: "2rem", color: "#6b7280" }}
                  />
                </TableContainer>
                {matrixPagerRange && matrixPagerRange.total > 0 ? (
                  <div className="ts-rep-matrix-pager d-flex flex-wrap align-items-center justify-content-between gap-2 mt-2 px-1">
                    <p className="small text-muted mb-0">
                      {t("app.timesheets.reports.matrixPagerSummary", {
                        from: matrixPagerRange.from,
                        to: matrixPagerRange.to,
                        total: matrixPagerRange.total,
                      })}
                    </p>
                    {matrixPagerRange.totalPages > 1 ? (
                      <div className="d-flex gap-2">
                        <button
                          type="button"
                          className="btn btn-outline-secondary btn-sm"
                          disabled={matrixPage <= 1 || matrixLoading}
                          onClick={() => setMatrixPage((p) => Math.max(1, p - 1))}
                        >
                          {t("app.timesheets.reports.matrixPagerPrev")}
                        </button>
                        <button
                          type="button"
                          className="btn btn-outline-secondary btn-sm"
                          disabled={matrixPage >= matrixPagerRange.totalPages || matrixLoading}
                          onClick={() => setMatrixPage((p) => Math.min(matrixPagerRange.totalPages, p + 1))}
                        >
                          {t("app.timesheets.reports.matrixPagerNext")}
                        </button>
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </section>
            </>
          ) : null}
        </>
      )}
    </div>
  );
};

export default TimeSheetReportsPage;
